import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';

/**
 * Cliente Socket.IO com conexão lazy.
 * A conexão só é estabelecida no primeiro `emit()` ou `on()`, evitando overhead
 * de rede e memória no bootstrap quando o realtime não é utilizado.
 */
@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {
  private wss: Socket | null = null;
  private readonly destroy$ = new Subject<void>();

  public emit(event: string, args: unknown): void {
    this.ensureConnection().emit(event, args);
  }

  public on<T = unknown>(event: string): Observable<T> {
    const socket: Socket = this.ensureConnection();
    return new Observable<T>((observer) => {
      const messageHandler = (message: T) => observer.next(message);
      const errorHandler = (error: unknown) => observer.error(error);
      socket.on(event, messageHandler);
      socket.on('connect_error', errorHandler);
      return () => {
        socket.off(event, messageHandler);
        socket.off('connect_error', errorHandler);
      };
    }).pipe(takeUntil(this.destroy$));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }

  /**
   * Garante que a conexão WebSocket exista antes de emitir ou escutar eventos.
   */
  private ensureConnection(): Socket {
    if (!this.wss) {
      this.connect();
    }
    return this.wss;
  }

  private connect(): void {
    const url: string = `${environment.socket.url}`;
    const options: Partial<ManagerOptions & SocketOptions> = {
      transports: ['websocket'],
      secure: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    };
    this.wss = io(url, options);
  }

  private disconnect(): void {
    this.wss?.disconnect();
    this.wss = null;
  }
}
